import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, User, GraduationCap, BookOpen, ClipboardList, CreditCard, Award, FileText, Bell, LifeBuoy, LogOut, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import apiClient from "@/services/apiClient";
import { studentNotificationService } from "@/services/notificationService";

const siteLogoPath = "/logo/gitlogo.jpg";

const NAV = [
  { to: "/student/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "student-nav-dashboard" },
  { to: "/student/profile", label: "Profile", icon: User, testid: "student-nav-profile" },
  { to: "/student/my-courses", label: "My Courses", icon: GraduationCap, testid: "student-nav-my-courses" },
  { to: "/student/bookings", label: "My Bookings", icon: BookOpen, testid: "student-nav-bookings" },
  { to: "/student/enrolments", label: "My Enrolments", icon: ClipboardList, testid: "student-nav-enrolments" },
  { to: "/student/payments", label: "Payments", icon: CreditCard, testid: "student-nav-payments" },
  { to: "/student/certificates", label: "Certificates", icon: Award, testid: "student-nav-certs" },
  { to: "/student/documents", label: "Documents", icon: FileText, testid: "student-nav-docs" },
  { to: "/student/notifications", label: "Notifications", icon: Bell, testid: "student-nav-notif" },
  { to: "/student/support", label: "Support Tickets", icon: LifeBuoy, testid: "student-nav-support" },
];

export default function StudentLayout() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [supportPending, setSupportPending] = useState(0);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");
        const response = await apiClient.get(`${apiBaseUrl}/StudentProfile`);
        setProfile(response.data);
      } catch (error) {
        console.error("Failed to load student profile", error);
      }
    };

    loadProfile();
  }, []);

  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        setUnreadNotifications(await studentNotificationService.getUnreadCount());
        const response = await apiClient.get("/StudentSupport/pending-count");
        setSupportPending(response.data?.count || 0);
      } catch (error) {
        console.error("Failed to load unread student notifications", error);
      }
    };

    loadUnreadCount();
    window.addEventListener("notifications:updated", loadUnreadCount);
    return () => window.removeEventListener("notifications:updated", loadUnreadCount);
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    navigate("/login", { replace: true });
  };

  const initials = `${profile?.firstName?.[0] || ""}${profile?.lastName?.[0] || ""}` || "S";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setOpen(!open)} className="lg:hidden p-1.5" aria-label="Menu">
              {open ? <X /> : <Menu />}
            </button>
            <Link to="/" className="flex items-center gap-3">
              <img
                src={siteLogoPath}
                alt="Gippsland Institute of Technology"
                className="h-10 w-auto object-contain sm:h-11"
              />
              <div className="hidden sm:block">
                <div className="text-[10px] text-slate-500 uppercase tracking-[0.18em]">Student Portal</div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 pr-3 border-r border-slate-200">
              {profile?.profilePhoto ? <img src={profile.profilePhoto} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-[#0A2540] text-white flex items-center justify-center text-xs font-bold">{initials}</div>}
              <div className="leading-tight">
                <div className="text-sm font-semibold">{profile ? `${profile.firstName} ${profile.lastName}` : "Loading..."}</div>
                <div className="text-xs text-slate-500">{profile ? `Student #${profile.studentId}` : ""}</div>
              </div>
            </div>
            <button onClick={logout} className="flex items-center gap-2 text-sm text-slate-600 hover:text-rose-600" data-testid="student-logout">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 py-6 grid lg:grid-cols-[240px_1fr] gap-6">
        {/* Sidebar */}
        <aside className={`${open ? "block" : "hidden"} lg:block`}>
          <nav className="bg-white border border-slate-200 rounded-xl p-2 sticky top-24" data-testid="student-sidebar">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={n.testid}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-[#0A2540] text-white" : "text-slate-700 hover:bg-slate-50"}`}>
                <span className="relative shrink-0">
                  <n.icon className="w-4 h-4" />
                  {((n.to === "/student/notifications" && unreadNotifications > 0) || (n.to === "/student/support" && supportPending > 0)) && (
                    <span className="absolute -right-1.5 -top-1.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                  )}
                </span>
                <span className="flex-1">{n.label}</span>
                {((n.to === "/student/notifications" && unreadNotifications > 0) || (n.to === "/student/support" && supportPending > 0)) && (
                  <span className="min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                    {n.to === "/student/support" ? (supportPending > 99 ? "99+" : supportPending) : (unreadNotifications > 99 ? "99+" : unreadNotifications)}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
